import { Box, Slide, styled, Typography } from "@mui/material";
import { WifiOff } from "@procore/core-icons";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "app/redux-store-interactions";
import { OFFLINE_INDICATOR_LAYOUT } from "components/toolbars/constants";
import { selectIsProcoreMobileDesign } from "slices/explorer";

import { useOfflineBarVariant } from "./useOfflineBarVariant";

// Visual spec from Figma "BIM - Offline Mode" / toast (node 198:32280):
// cream background, dark text, Inter Semi-Bold 14/20, drop-shadow,
// only the bottom corners rounded (it drops down from the top edge).
const Toast = styled(Box)({
    pointerEvents: "auto",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    padding: "4px 24px",
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    background: "#FDF6E2",
    color: "#313435",
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
    fontFamily: "Inter, sans-serif",
    fontSize: 14,
    fontWeight: 600,
    lineHeight: "20px",
    letterSpacing: 0.15,
    userSelect: "none",
    whiteSpace: "nowrap",
    "& svg": {
        color: "#C9A227",
        width: 24,
        height: 24,
    },
});

/**
 * Persistent visual indicator shown while the model is fully synced for offline
 * use (`view.data` available) but the browser reports no network connectivity.
 * This is the "as today" subtle chip from the Figma spec.
 */
export function OfflineIndicator() {
    const { t } = useTranslation();
    const variant = useOfflineBarVariant();
    // Slide needs a stable container ref to anchor the transform off of.
    const containerRef = useRef<HTMLDivElement | null>(null);

    return (
        <Box ref={containerRef} sx={{ overflow: "hidden", pointerEvents: "none" }}>
            <Slide
                in={variant === "synced"}
                direction="down"
                container={containerRef.current}
                mountOnEnter
                unmountOnExit
                timeout={{ enter: 260, exit: 200 }}
            >
                <Box>
                    <Toast data-testid="offline-indicator" role="status" aria-live="polite">
                        <WifiOff size="md" />
                        <Typography
                            component="span"
                            sx={{
                                fontFamily: "inherit",
                                fontSize: "inherit",
                                fontWeight: "inherit",
                                lineHeight: "inherit",
                                letterSpacing: "inherit",
                                color: "inherit",
                            }}
                        >
                            {t("offlineIndicator")}
                        </Typography>
                    </Toast>
                </Box>
            </Slide>
        </Box>
    );
}

const Bar = styled(Box)({
    width: "100%",
    minHeight: OFFLINE_INDICATOR_LAYOUT.HEIGHT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    padding: "4px 24px",
    background: "#FDF6E2",
    color: "#313435",
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
    fontFamily: "Inter, sans-serif",
    fontSize: 14,
    lineHeight: "20px",
    letterSpacing: 0.15,
    userSelect: "none",
    "& svg": {
        flexShrink: 0,
        color: "#C9A227",
        width: 24,
        height: 24,
    },
});

/**
 * Full-width no-internet-connection bar shown while connectivity is lost and the
 * model is not fully synced (`view.data` unavailable). Rendered in layout flow so
 * the viewer content is shifted down rather than overlaid.
 */
export function OfflineConnectionBar() {
    const { t } = useTranslation();
    const isProcoreMobileDesign = useAppSelector(selectIsProcoreMobileDesign);

    return (
        <Bar data-testid="offline-connection-bar" role="status" aria-live="polite">
            <WifiOff size="md" />
            <Typography
                component="span"
                sx={{
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    lineHeight: "inherit",
                    letterSpacing: "inherit",
                    color: "inherit",
                    textAlign: "center",
                }}
            >
                <Box component="span" sx={{ fontWeight: 600 }}>
                    {t("noInternetConnection")}.
                </Box>{" "}
                {t(isProcoreMobileDesign ? "downloadThisModelWhileOnline" : "someFeaturesMayNotWork")}
            </Typography>
        </Bar>
    );
}
